import React, { useEffect, useState } from 'react';
import type { Dispatch } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import SmartUserPicker from '@atlaskit/smart-user-picker';
import type { OptionData } from '@atlaskit/user-picker';
import { ErrorMessage } from '@atlaskit/form';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	FilterHeaderCompiled,
	InputContainerCompiled,
	SelectContainerCompiled,
} from './presentationalComponents.tsx';
import type { FiltersState } from './useFiltersReducer.ts';
import { FilterKeys } from './filtersDefinitions.ts';

const i18n = defineMessages({
	creatorLabel: {
		id: 'home.views.tasks.filters.label.creator',
		defaultMessage: 'Creator',
		description:
			"The label for a creator selector where it is filled with a list of task creators for it's options.",
	},

	creatorPlaceholderMessage: {
		id: 'home.views.tasks.filters.creator.placeholder',
		defaultMessage: 'Search or select by name',
		description: 'The placeholder message for the user picker',
	},

	creatorMaxUsersLabel: {
		id: 'home.views.tasks.filters.label.creator.max.users',
		defaultMessage: 'Maximum reached',
		description: 'The label displayed when the maximum number of users has been selected',
	},

	creatorNumberUsersLabel: {
		id: 'home.views.tasks.filters.label.creator.number.users',
		defaultMessage: 'add up to 10 people',
		description: 'The placeholder message displayed once one user has been added to the filter',
	},
});

const MAX_SELECTED_USERS = 10;
interface SelectUserCreatorProps {
	filterDispatch: Dispatch<any>;
	filterState: FiltersState;
}

export const SelectCreator = ({ filterDispatch, filterState }: SelectUserCreatorProps) => {
	const { formatMessage } = useIntl();
	const [selectedUsers, setSelectedUsers] = useState<OptionData[]>([]);
	const [disableInput, setDisableInput] = useState<boolean>(false);
	const { cloudId, userId } = useSessionData();

	useEffect(() => {
		if (filterState.filters.creator) {
			setSelectedUsers(filterState.filters[FilterKeys.CREATOR] || []);
			setDisableInput(filterState.filters.creator.length >= MAX_SELECTED_USERS);
		} else {
			setSelectedUsers([]);
			setDisableInput(false);
		}
		const input = document.getElementById('react-select-contributors-input');
		if (input) {
			input.style.width = '110%';
		}
	}, [filterState]);

	const onChange = (contributors) => {
		if (contributors) {
			setSelectedUsers(contributors);
			setDisableInput(contributors.length >= MAX_SELECTED_USERS);
		}
	};

	const onBlur = () => {
		filterDispatch({
			type: FilterKeys.CREATOR,
			payload: selectedUsers,
		});
	};

	return (
		<InputContainerCompiled>
			<FilterHeaderCompiled htmlFor="creator-filter">
				{formatMessage(i18n.creatorLabel)}
			</FilterHeaderCompiled>
			<SelectContainerCompiled>
				<SmartUserPicker
					fieldId="contributors"
					isMulti
					maxOptions={10}
					onChange={onChange}
					onBlur={onBlur}
					principalId={userId ?? undefined}
					productKey="confluence"
					siteId={cloudId}
					placeholder={formatMessage(i18n.creatorPlaceholderMessage)}
					value={selectedUsers}
					noBorder
					addMoreMessage={disableInput ? '' : formatMessage(i18n.creatorNumberUsersLabel)}
					disableInput={disableInput}
					displayEmailInByline
					enableEmailSearch
					allowEmailSelectionWhenEmailMatched={false}
				/>
				{disableInput && <ErrorMessage>{formatMessage(i18n.creatorMaxUsersLabel)}</ErrorMessage>}
			</SelectContainerCompiled>
		</InputContainerCompiled>
	);
};
